import moment from 'moment';
import React, { PureComponent, PropTypes } from 'react';
import { RouteTransition, presets } from 'react-router-transition';
import { browserHistory } from 'react-router';
import { map as _map } from 'lodash';

import * as bookingHelpers from '../../util/bookingHelpers';

import {
    BOOKING_STATUS_CHECKED_OUT,
    BOOKING_STATUS_CHECKED_IN,
    BOOKING_STATUS_CANCELLED,
} from '../../constants/booking';
import {
    START_TIME,
    END_TIME,
} from '../../constants/timelineFocusPoints';
import {
    HAMBURGER_MENU,
    TIME_LINK,
} from '../../constants/editTimeReferrer';

import ErrorModalContainer from '../../containers/ErrorModalContainer';
import LoadingModalContainer from '../../containers/LoadingModalContainer';
import BookingItem from './BookingItem';
import BookingDetailsContainer from '../../containers/BookingDetailsContainer';
import EditBookingTimeSelectorContainer from '../../containers/EditBookingTimeSelectorContainer';
import MoreMenu from '../Common/MoreMenu';
import Snackbar from '../Common/Snackbar';

import './Booking.css';

export default class Booking extends PureComponent {
    static propTypes = {
        booking: PropTypes.object,
        plant: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired,
        availabilityWindow: PropTypes.object,
        cancelBooking: PropTypes.func.isRequired,
        updateBookingTimes: PropTypes.func.isRequired,
        trackEditBookingTimeClick: PropTypes.func.isRequired,
        checkInBooking: PropTypes.func.isRequired,
        checkOutBooking: PropTypes.func.isRequired,
        clickEditBookingTime: PropTypes.func.isRequired,
    }

    constructor(props) {
        super(props);

        this.state = {
            bookingChanged: false,
            selfChanged: false,
            editingTime: false,
            editTimeFocusPoint: START_TIME,
        };

        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleCheckInClick = this.handleCheckInClick.bind(this);
        this.handleCheckOutClick = this.handleCheckOutClick.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleRequestChangeTime = this.handleRequestChangeTime.bind(this);
        this.handleNewTimeSelected = this.handleNewTimeSelected.bind(this);
        this.handleCancelTimeSelected = this.handleCancelTimeSelected.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (!nextProps.booking || nextProps.booking.statusId === BOOKING_STATUS_CANCELLED) {
            browserHistory.replace(`/bookings`);
        } else {
            this.setState({
                bookingChanged: this.props.booking.versionedUId !== nextProps.booking.versionedUId && !this.state.selfChanged,
                selfChanged: false,
            });
        }
    }

    handleCloseClick() {
        browserHistory.push(`/bookings`);
    }

    handleCheckInClick() {
        // Note: we're doing this as we need to make sure the state change happens first.
        this.setState(
            { selfChanged: true },
            () => {
                this.props.checkInBooking(
                    this.props.booking.versionedUId,
                    this.props.booking.staticUId,
                );
            },
        );
    }

    handleCheckOutClick() {
        this.setState(
            { selfChanged: true },
            () => {
                this.props.checkOutBooking(
                    this.props.booking.versionedUId,
                    this.props.booking.staticUId,
                );
            },
        );
    }

    handleCancelClick() {
        this.setState(
            { selfChanged: true },
            () => {
                this.props.cancelBooking(
                    this.props.booking.versionedUId,
                    this.props.booking.staticUId,
                );
            },
        );
    }

    handleRequestChangeTime(selectedPoint, caller) {
        this.props.trackEditBookingTimeClick(this.props.booking.staticUId, caller);
        this.props.clickEditBookingTime(this.props.booking.staticUId, this.props.booking.versionedUId);

        this.setState({
            selfChanged: true, // Should this be here?
            editingTime: true,
            editTimeFocusPoint: selectedPoint,
        });
    }

    handleCancelTimeSelected() {
        this.setState({ editingTime: false });
    }

    handleNewTimeSelected(start, end) {
        this.setState(
            {
                editingTime: false,
                selfChanged: true,
            },
            () => {
                this.props.updateBookingTimes(
                    this.props.booking.versionedUId,
                    this.props.booking.staticUId,
                    start,
                    end,
                );
            },
        );
    }

    generateMenuItems(noConflictingTimes) {
        let menuItems = [];
        const { booking } = this.props;
        const allowedStatusIdTransitions = booking.actionPermissions.allowedStatusIdTransitions;

        if (noConflictingTimes) {
            // If can edit both then we implicitly choose going to the start time
            if (bookingHelpers.canEditStartTime(this.props.booking.statusId, booking.actionPermissions.canEditStartTime)) {
                menuItems.push(<li onClick={() => this.handleRequestChangeTime(START_TIME, HAMBURGER_MENU)} key="edit-start-time">Edit times</li>);
            } else if (bookingHelpers.canEditEndTime(this.props.booking.statusId, booking.actionPermissions.canEditEndTime)) {
                menuItems.push(<li onClick={() => this.handleRequestChangeTime(END_TIME, HAMBURGER_MENU)} key="edit-end-time">Edit times</li>);
            }
        }

        if (allowedStatusIdTransitions && allowedStatusIdTransitions.length > 0) {
            menuItems = menuItems.concat(_map(allowedStatusIdTransitions, (allowedStatusId) => {
                switch (allowedStatusId) {
                    case BOOKING_STATUS_CHECKED_OUT:
                        return <li onClick={this.handleCheckOutClick} key="check-out">Check out</li>;
                    case BOOKING_STATUS_CHECKED_IN:
                        return <li onClick={this.handleCheckInClick} key="check-in">Check in</li>;
                    case BOOKING_STATUS_CANCELLED:
                        return <li onClick={this.handleCancelClick} key="cancel">Cancel booking</li>;
                    default:
                        return null;
                }
            }));
        }

        if (menuItems.length === 0) {
            return null;
        }

        return (
            <MoreMenu>
                {menuItems}
            </MoreMenu>
        );
    }

    buildMainPlantString(mainPlant) {
        const vehiclePlantPart = mainPlant.rego && mainPlant.year
            ? `${this.retrieveValueFromProperty(mainPlant.rego)} - ${this.retrieveValueFromProperty(mainPlant.year)} `
            : ``;

        const allPlantPart = mainPlant.make && mainPlant.model
            ? `${this.retrieveValueFromProperty(mainPlant.make)} ${this.retrieveValueFromProperty(mainPlant.model)}`
            : ``;

        return `${vehiclePlantPart}${allPlantPart}`;
    }

    retrieveValueFromProperty(prop) {
        return prop !== null ? prop : ``;
    }

    render() {
        const { booking, plant } = this.props;

        if (!booking) {
            return <div />;
        }

        const bookingTimes = bookingHelpers.getStartEndTimesBasedOnBookingStatus(booking);

        // If we haven't fetched availability yet, or there are no conflicts preventing time edits
        const noConflictingTimes = !this.props.availabilityWindow || (this.props.availabilityWindow && this.props.availabilityWindow.valid);
        const menuJsx = this.generateMenuItems(noConflictingTimes);

        const headerClassName = bookingTimes.start.isSame(moment(), `d`) ? `today` : ``;
        const bookingStatusCssClass = bookingHelpers.bookingStatusToCssClass(booking.statusId);

        const mainPlant = bookingHelpers.getBookingPlant(booking, plant);
        if (!mainPlant) {
            return null;
        }

        const snackbarJsx = this.state.bookingChanged && !this.state.selfChanged
            ? <Snackbar message="This booking has been updated to reflect changes made elsewhere." />
            : null;

        const hidingCssClass = this.state.editingTime ? `hiding` : ``;

        // TODO: Can we also move the callbacks to the container?
        const timeSelectorJsx = this.state.editingTime ? (
            <EditBookingTimeSelectorContainer
                booking={booking}
                focusPoint={this.state.editTimeFocusPoint}
                onSave={this.handleNewTimeSelected}
                onClose={this.handleCancelTimeSelected}
            />) : null;

        const now = moment();
        const startTimeBeforeNow = bookingTimes.start.isBefore(now);
        const endTimeBeforeNow = bookingTimes.end.isBefore(now);

        const startTimeOnClick = !startTimeBeforeNow && noConflictingTimes && bookingHelpers.canEditStartTime(this.props.booking.statusId, this.props.booking.actionPermissions.canEditStartTime) ?
            () => this.handleRequestChangeTime(START_TIME, TIME_LINK) :
            null;

        const endTimeOnClick = !endTimeBeforeNow && noConflictingTimes && bookingHelpers.canEditEndTime(this.props.booking.statusId, this.props.booking.actionPermissions.canEditEndTime) ?
            () => this.handleRequestChangeTime(END_TIME, TIME_LINK) :
            null;

        return (
            <div>
                <RouteTransition
                    component={false}
                    className="transition-wrapper"
                    pathname={this.props.location.pathname}
                    {...presets.pop}
                >
                    <div className={`booking ${hidingCssClass}`}>
                        <header className={`${headerClassName} ${bookingStatusCssClass}`}>
                            <div className="titles">
                                <h1>{mainPlant.name}</h1>
                                <h3>{bookingHelpers.bookingStatusToText(booking.statusId)}</h3>
                            </div>
                            <i className="material-icons close-button" onClick={this.handleCloseClick}>&#xE5CD;</i>
                            {menuJsx}
                        </header>
                        <div className="content">
                            <BookingItem title="Plant" value={this.buildMainPlantString(mainPlant)} />
                            <BookingDetailsContainer
                                booking={booking}
                                bookingTimes={bookingTimes}
                                startTimeOnClick={startTimeOnClick}
                                endTimeOnClick={endTimeOnClick}
                            />
                        </div>
                    </div>
                </RouteTransition>
                {timeSelectorJsx}
                <LoadingModalContainer />
                <ErrorModalContainer />
                {snackbarJsx}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./scripts/components/Booking/Booking.jsx